import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authFetch } from './api';
import './ClaudeMemoryImport.css';

const API_URL = process.env.REACT_APP_API_URL || '';
export const CLAUDE_MEMORY_KEY = 'THOUGHTCLAW_CLAUDE_MEMORY';

export function readClaudeMemory() {
  try {
    const pack = JSON.parse(localStorage.getItem(CLAUDE_MEMORY_KEY) || 'null');
    return pack?.enabled && pack?.context ? pack : null;
  } catch { return null; }
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function relativeDate(value) {
  if (!value) return 'Recently';
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
  if (days < 1) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function mergePacks(previous, update) {
  if (!previous) return update;
  if (previous.schemaVersion !== update.schemaVersion) return update;
  const imported = Object.keys({ ...previous.imported, ...update.imported }).reduce((result, key) => {
    result[key] = (previous.imported?.[key] || 0) + (update.imported?.[key] || 0);
    return result;
  }, {});
  return {
    ...update,
    context: `${previous.context}\n\n${update.context}`.slice(-180000),
    imported,
    topics: [...new Set([...(previous.topics || []), ...(update.topics || [])])].slice(0, 24),
    artifacts: [...(previous.artifacts || []), ...(update.artifacts || [])].slice(-180),
    artifactTypes: Object.entries({ ...(previous.artifactTypes || {}), ...(update.artifactTypes || {}) }).reduce((result, [type]) => {
      result[type] = (previous.artifactTypes?.[type] || 0) + (update.artifactTypes?.[type] || 0);
      return result;
    }, {}),
    sourceIds: [...new Set([...(previous.sourceIds || []), ...(update.sourceIds || [])])],
  };
}

function SourceHeader({ icon, name, description, stats, selected, onToggle, children }) {
  return (
    <section className={`cm-source ${selected ? 'is-selected' : ''}`}>
      <div className="cm-source-main">
        <button className="cm-check" aria-pressed={selected} onClick={onToggle}><span>{selected ? '✓' : ''}</span></button>
        <span className="cm-source-icon">{icon}</span>
        <div className="cm-source-copy"><strong>{name}</strong><p>{description}</p></div>
        <div className="cm-source-stats">{stats.map(([value, label]) => <span key={label}><b>{formatCount(value)}</b>{label}</span>)}</div>
      </div>
      {children}
    </section>
  );
}

function CompletedPack({ pack, onStart }) {
  const artifactTypes = Object.entries(pack.artifactTypes || {}).sort((a, b) => b[1] - a[1]).slice(0, 10);
  return (
    <section className="cm-complete">
      <div className="cm-complete-mark">✓</div>
      <div className="cm-complete-copy">
        <span>MEMORY PACK ACTIVE</span>
        <h2>Your Claude history can now think with you.</h2>
        <p>Future missions will begin with this context attached. ThoughtClaw will preserve established decisions, notice contradictions, and build from prior work.</p>
      </div>
      <button className="cm-primary" onClick={onStart}>Start with this memory <span>↗</span></button>
      <div className="cm-pack-stats">
        <span><b>{formatCount((pack.imported?.codeSessions || 0) + (pack.imported?.coworkSessions || 0))}</b>sessions</span>
        <span><b>{formatCount(pack.imported?.messages)}</b>messages</span>
        <span><b>{formatCount(pack.imported?.artifacts)}</b>artifacts</span>
        <span><b>{formatCount(pack.topics?.length)}</b>remembered topics</span>
      </div>
      {!!pack.topics?.length && <div className="cm-topics"><small>WHAT THOUGHTCLAW CAN NOW RECALL</small><div>{pack.topics.slice(0, 10).map(topic => <span key={topic}>{topic}</span>)}</div></div>}
      {!!artifactTypes.length && <div className="cm-artifacts"><small>ARTIFACT INVENTORY</small><div>{artifactTypes.map(([type, count]) => <span key={type}><b>{count}</b> .{type}</span>)}</div></div>}
    </section>
  );
}

export default function ClaudeMemoryImport({ onClose, onStartMission }) {
  const isLocalHost = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  }, []);
  const [scan, setScan] = useState(null);
  const [pack, setPack] = useState(() => readClaudeMemory());
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [codeOpen, setCodeOpen] = useState(false);
  const [coworkOpen, setCoworkOpen] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [selectedCowork, setSelectedCowork] = useState(new Set());
  const [pastedText, setPastedText] = useState('');
  const fileRef = useRef(null);

  const runScan = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_URL}/api/integrations/claude-memory/scan`);
      if (!response.ok) throw new Error(`Local bridge returned ${response.status}`);
      const result = await response.json();
      setScan(result);
      setSelectedProjects(new Set(result.sources?.code?.projects?.map(item => item.id) || []));
      setSelectedCowork(new Set(result.sources?.cowork?.sessions?.map(item => item.id) || []));
    } catch (err) {
      setError(err.message || 'Local Claude history is unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runScan(); }, [runScan]);

  const hasSelection = selectedProjects.size + selectedCowork.size > 0;
  const isUpdate = !!pack;
  const needsSafeRebuild = isUpdate && pack.schemaVersion !== 2;
  const totalSessions = Number(scan?.totals?.sessions || 0);
  const remoteZeroScan = !!scan && !isLocalHost && totalSessions === 0 && !pack;
  const selectedStats = useMemo(() => ({ projects: selectedProjects.size, cowork: selectedCowork.size }), [selectedProjects, selectedCowork]);
  const newSelectionCount = useMemo(() => {
    if (needsSafeRebuild) return selectedProjects.size + selectedCowork.size;
    const imported = new Set(pack?.sourceIds || []);
    return [...selectedProjects].filter(id => !imported.has(`code:${id}`)).length
      + [...selectedCowork].filter(id => !imported.has(`cowork:${id}`)).length;
  }, [needsSafeRebuild, pack?.sourceIds, selectedCowork, selectedProjects]);
  const canImport = hasSelection && (!isUpdate || newSelectionCount > 0);
  const sectionLabel = isUpdate ? 'KEEP MEMORY CURRENT' : remoteZeroScan ? 'CLOUD APP CANNOT SCAN YOUR MAC' : isLocalHost ? 'FOUND ON THIS MAC' : 'LOCAL BRIDGE CHECK';
  const sectionTitle = isUpdate ? `Last imported ${relativeDate(pack.createdAt)}` : remoteZeroScan ? 'Paste or upload your Claude export' : 'Choose what ThoughtClaw should remember';
  const importStatus = remoteZeroScan
    ? 'Use the paste/upload fallback below, or run ThoughtClaw locally to scan this Mac.'
    : needsSafeRebuild
      ? 'Rebuild once to apply stronger credential redaction'
      : isUpdate && newSelectionCount === 0
        ? 'No new local sessions since your last import'
        : 'Imported memory is automatically attached to future missions';
  const importLabel = remoteZeroScan
    ? 'Use paste/upload below'
    : importing
      ? 'Building memory pack…'
      : needsSafeRebuild
        ? 'Rebuild memory safely'
        : isUpdate && newSelectionCount === 0
          ? 'Memory is up to date'
          : isUpdate
            ? `Import ${newSelectionCount} updates`
            : 'Import selected memory';

  const toggleItem = (setter, id) => setter(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const importSelected = useCallback(async () => {
    setImporting(true);
    setError('');
    try {
      const response = await authFetch(`${API_URL}/api/integrations/claude-memory/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codeProjectIds: [...selectedProjects],
          coworkSessionIds: [...selectedCowork],
          previousSourceIds: needsSafeRebuild ? [] : (pack?.sourceIds || []),
        }),
      });
      if (!response.ok) throw new Error(`Import failed with ${response.status}`);
      const update = await response.json();
      const merged = mergePacks(pack, update);
      localStorage.setItem(CLAUDE_MEMORY_KEY, JSON.stringify(merged));
      setPack(merged);
    } catch (err) {
      setError(err.message || 'Claude memory could not be imported.');
    } finally {
      setImporting(false);
    }
  }, [needsSafeRebuild, pack, selectedCowork, selectedProjects]);

  const importPasted = useCallback(() => {
    if (!pastedText.trim()) return;
    const update = {
      id: `claude_memory_${Date.now()}`,
      name: 'Claude memory',
      createdAt: new Date().toISOString(),
      enabled: true,
      context: `IMPORTED CLAUDE MEMORY — User-provided conversation export:\n\n${pastedText.slice(0, 90000)}`,
      imported: { codeProjects: 0, codeSessions: 0, coworkSessions: 1, messages: 1, artifacts: 0 },
      topics: ['Imported Claude conversation'], artifactTypes: {}, artifacts: [], sourceIds: [`paste:${Date.now()}`],
    };
    const merged = mergePacks(pack, update);
    localStorage.setItem(CLAUDE_MEMORY_KEY, JSON.stringify(merged));
    setPack(merged);
    setPastedText('');
  }, [pack, pastedText]);

  const handleFile = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = result => setPastedText(String(result.target?.result || ''));
    reader.readAsText(file);
  };

  return (
    <div className="cm-page">
      <header className="cm-hero">
        <div>
          <span className="cm-eyebrow">CONTINUITY, NOT ANOTHER FRESH START</span>
          <h1>Bring your Claude history with you.</h1>
          <p>{isLocalHost ? 'Turn the decisions, chats, code work, and artifacts already sitting on this Mac into memory that ThoughtClaw can use across every mission.' : 'Bring Claude Code and Cowork history into ThoughtClaw by uploading an export, pasting a conversation, or using the local ThoughtClaw bridge on your Mac.'}</p>
        </div>
        {onClose && <button className="cm-close" onClick={onClose}>Close</button>}
      </header>

      <div className="cm-trust"><span>⌾</span><div><b>{isLocalHost ? 'Local and read-only' : 'Cloud-safe import'}</b><p>{isLocalHost ? 'The inventory is scanned on this Mac. Conversation bodies are not sent to the browser or any model until you explicitly import them. Secrets, tool results, hidden reasoning, and audit credentials are excluded.' : 'The hosted app cannot inspect your Mac directly. Upload or paste only the Claude context you want remembered; secrets, tool results, hidden reasoning, and audit credentials are still excluded during memory building.'}</p></div></div>

      {pack && <CompletedPack pack={pack} onStart={onStartMission} />}

      <section className="cm-scan">
        <div className="cm-section-head">
          <div><span>{sectionLabel}</span><h2>{sectionTitle}</h2></div>
          {scan && <div className="cm-total"><b>{formatCount(totalSessions)}</b><span>sessions detected</span></div>}
        </div>

        {loading && <div className="cm-loading"><i /><span>Scanning Claude Code and Cowork locally…</span></div>}
        {error && <div className="cm-error"><b>Local history is not available</b><p>{error}</p><button onClick={runScan}>Try local bridge again</button></div>}
        {remoteZeroScan && <div className="cm-remote-note"><b>The cloud app cannot read laptop files.</b><p>That zero-session scan is expected in production. To keep this one-click on your Mac, open the local ThoughtClaw app; otherwise paste or upload a Claude export below.</p></div>}

        {scan && (
          <div className="cm-sources">
            <SourceHeader
              icon="◇" name="Claude Code" description="Projects, conversations, plans, and persistent project memory"
              selected={selectedProjects.size > 0}
              onToggle={() => setSelectedProjects(selectedProjects.size ? new Set() : new Set(scan.sources.code.projects.map(item => item.id)))}
              stats={[[scan.sources.code.projectCount, 'projects'], [scan.sources.code.sessionCount, 'sessions'], [scan.sources.code.messageCount, 'messages']]}
            >
              {!!scan.sources.code.projects.length && <button className="cm-review" onClick={() => setCodeOpen(value => !value)}>{codeOpen ? 'Hide projects' : 'Review projects'} <span>{codeOpen ? '↑' : '↓'}</span></button>}
              {codeOpen && <div className="cm-rows">{scan.sources.code.projects.map(item => <label key={item.id}><input type="checkbox" checked={selectedProjects.has(item.id)} onChange={() => toggleItem(setSelectedProjects, item.id)} /><span><b>{item.name}</b><small>{item.sessionCount} sessions · {relativeDate(item.updatedAt)}</small></span></label>)}</div>}
            </SourceHeader>

            <SourceHeader
              icon="✦" name="Claude Cowork" description="Collaborative sessions plus the documents, code, media, and files they created"
              selected={selectedCowork.size > 0}
              onToggle={() => setSelectedCowork(selectedCowork.size ? new Set() : new Set(scan.sources.cowork.sessions.map(item => item.id)))}
              stats={[[scan.sources.cowork.sessionCount, 'sessions'], [scan.sources.cowork.messageCount, 'messages'], [scan.sources.cowork.artifactCount, 'artifacts']]}
            >
              {!!scan.sources.cowork.sessions.length && <button className="cm-review" onClick={() => setCoworkOpen(value => !value)}>{coworkOpen ? 'Hide sessions' : 'Review sessions'} <span>{coworkOpen ? '↑' : '↓'}</span></button>}
              {coworkOpen && <div className="cm-rows cm-rows-cowork">{scan.sources.cowork.sessions.map(item => <label key={item.id}><input type="checkbox" checked={selectedCowork.has(item.id)} onChange={() => toggleItem(setSelectedCowork, item.id)} /><span><b>{item.title}</b><small>{item.messageCount} messages · {item.artifactCount} artifacts · {relativeDate(item.updatedAt)}</small></span></label>)}</div>}
            </SourceHeader>
          </div>
        )}

        {scan && <div className="cm-import-bar"><div><b>{selectedStats.projects} projects + {selectedStats.cowork} Cowork sessions</b><span>{importStatus}</span></div><button className="cm-primary" disabled={!canImport || importing || remoteZeroScan} onClick={importSelected}>{importLabel} <span>{canImport && !remoteZeroScan ? '→' : remoteZeroScan ? '↓' : '✓'}</span></button></div>}
      </section>

      <details className="cm-manual" open={remoteZeroScan || undefined}>
        <summary>Can’t access local history? Paste or upload an export <span>＋</span></summary>
        <div><textarea value={pastedText} onChange={event => setPastedText(event.target.value)} placeholder="Paste a Claude conversation or exported JSON here…" rows={6} /><input ref={fileRef} type="file" accept=".json,.txt,.md" onChange={handleFile} hidden /><button onClick={() => fileRef.current?.click()}>Choose file</button><button className="cm-primary" disabled={!pastedText.trim()} onClick={importPasted}>Import export</button></div>
      </details>
    </div>
  );
}
